import { loader } from '@monaco-editor/react'
import * as monaco from 'monaco-editor'
import editorWorker from 'monaco-editor/editor/editor.worker.js?worker'

import { exerciseNames, type CodeProblem } from './code-problems'

/**
 * Monaco wired for MixedScript, self-hosted: the language ships with the app, the way
 * everything else does — no CDN between an editor and its syntax colours.
 */

self.MonacoEnvironment = { getWorker: () => new editorWorker() }
loader.config({ monaco })

export const LANGUAGE_ID = 'mixedscript'

const KEYWORDS = [
  'mixedscript',
  'template',
  'workout',
  'exercise',
  'superset',
  'choose',
  'rotate',
  'dynamic',
  'yield',
  'return',
  'let',
  'const',
  'if',
  'else',
  'for',
  'of',
  'warmup',
  'rest',
  'video',
  'auto',
]

const BUILTINS = ['range', 'amrap', 'e1rm', 'rpe', 'min', 'max', 'floor', 'ceil', 'round', 'abs']

let registered = false

export function registerMixedScript(): void {
  if (registered) {
    return
  }
  registered = true

  monaco.languages.register({ id: LANGUAGE_ID })

  monaco.languages.setMonarchTokensProvider(LANGUAGE_ID, {
    keywords: KEYWORDS,
    builtins: BUILTINS,
    tokenizer: {
      root: [
        [/\/\/.*$/, 'comment'],
        [/"/, 'string', '@string'],
        [/\d+(\.\d+)?(kg|lb|meters|miles|km|s|m)\b/, 'number.unit'],
        [/\d+(\.\d+)?%/, 'number.percent'],
        [/\d+(\.\d+)?/, 'number'],
        [/\b(free|sequential|strict|true|false)\b/, 'type'],
        [
          /[a-zA-Z_]\w*/,
          {
            cases: {
              '@keywords': 'keyword',
              '@builtins': 'type.builtin',
              '@default': 'identifier',
            },
          },
        ],
        [/[+\-*/%=!<>&|?:]+/, 'operator'],
        [/[{}()[\]]/, '@brackets'],
        [/[,.]/, 'delimiter'],
      ],
      string: [
        [/[^"\\]+/, 'string'],
        [/\\./, 'string.escape'],
        [/"/, 'string', '@pop'],
      ],
    },
  })

  monaco.languages.setLanguageConfiguration(LANGUAGE_ID, {
    comments: { lineComment: '//' },
    brackets: [
      ['{', '}'],
      ['(', ')'],
    ],
    autoClosingPairs: [
      { open: '{', close: '}' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
    ],
    indentationRules: {
      increaseIndentPattern: /\{[^}]*$/,
      decreaseIndentPattern: /^\s*\}/,
    },
  })

  monaco.languages.registerCompletionItemProvider(LANGUAGE_ID, {
    triggerCharacters: ['"'],
    provideCompletionItems: (model, position) => {
      const word = model.getWordUntilPosition(position)
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      }
      const before = model.getLineContent(position.lineNumber).slice(0, position.column - 1)

      // Inside `exercise "…` the catalog is what completes, not the keywords.
      const naming = /exercise\s+"([^"]*)$/.exec(before)
      if (naming !== null) {
        const partial = (naming[1] ?? '').toLowerCase()
        const nameRange = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: position.column - (naming[1]?.length ?? 0),
          endColumn: position.column,
        }
        return {
          suggestions: exerciseNames.current
            .filter((name) => partial === '' || name.toLowerCase().includes(partial))
            .map((name) => ({
              label: name,
              kind: monaco.languages.CompletionItemKind.Value,
              insertText: name,
              range: nameRange,
              sortText: (name.toLowerCase().startsWith(partial) ? '0' : '1') + name,
            })),
        }
      }

      return {
        suggestions: [
          ...KEYWORDS.map((keyword) => ({
            label: keyword,
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: keyword,
            range,
          })),
          ...BUILTINS.map((builtin) => ({
            label: builtin,
            kind: monaco.languages.CompletionItemKind.Function,
            insertText: builtin,
            range,
          })),
        ],
      }
    },
  })

  monaco.editor.defineTheme('mixedscript-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'keyword', foreground: '8ab4f8', fontStyle: 'bold' },
      { token: 'type', foreground: '81c995' },
      { token: 'type.builtin', foreground: 'fdd663' },
      { token: 'string', foreground: 'f28b82' },
      { token: 'string.escape', foreground: 'fdd663' },
      { token: 'number', foreground: 'b5cea8' },
      { token: 'number.unit', foreground: 'b5cea8' },
      { token: 'number.percent', foreground: 'b5cea8' },
      { token: 'comment', foreground: '9aa0a6', fontStyle: 'italic' },
      { token: 'operator', foreground: 'e8eaed' },
      { token: 'identifier', foreground: 'c3e7ff' },
      { token: 'delimiter', foreground: 'e8eaed' },
    ],
    colors: {
      'editor.background': '#131314',
      'editor.lineHighlightBackground': '#202124',
      'editorLineNumber.foreground': '#5f6368',
    },
  })
}

/** The one problem a script has, drawn as a squiggle where it lives. */
export function setProblemMarker(
  model: monaco.editor.ITextModel,
  problem: CodeProblem | null,
): void {
  monaco.editor.setModelMarkers(
    model,
    'mixedscript',
    problem === null
      ? []
      : [
          {
            severity: monaco.MarkerSeverity.Error,
            message: problem.message,
            startLineNumber: Math.max(1, problem.line),
            startColumn: Math.max(1, problem.column),
            endLineNumber: Math.max(1, problem.line),
            endColumn: Math.max(1, problem.column + 1),
          },
        ],
  )
}
